import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";

import {
  GetServicesQuery,
  GetServicesQueryVariables,
  SelectionKey,
  Service,
} from "../../../API";
import { GET_SERVICES } from "../../../common/operations/queries";

interface UseGetServicesByLocationResult {
  servicesByDeviceId: Map<string, Service[]>;
  loading: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch services by location and group them by deviceId.
 * Uses the getServices query with locationId parameter to efficiently
 * fetch all services for a location in a single query.
 *
 * @param {string} [locationId] - The location ID to fetch services for
 * @return {UseGetServicesByLocationResult} Object containing servicesByDeviceId map, loading state, and refetch function
 */
export const useGetServicesByLocation = (
  locationId?: string
): UseGetServicesByLocationResult => {
  const [getServices, { data, loading }] = useLazyQuery<
    GetServicesQuery,
    GetServicesQueryVariables
  >(GET_SERVICES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (locationId) {
      getServices({
        variables: {
          locationId,
          modelAlertSettingsSelectionKey: SelectionKey.keys,
        },
      });
    }
  }, [locationId, getServices]);

  const refetch = () => {
    if (locationId) {
      getServices({
        variables: {
          locationId,
          modelAlertSettingsSelectionKey: SelectionKey.keys,
        },
      });
    }
  };

  // Group services by deviceId for efficient lookup
  const servicesByDeviceId = useMemo(() => {
    const map = new Map<string, Service[]>();
    const items = data?.getServices?.items ?? [];

    for (const service of items) {
      if (!service) continue;

      const deviceId = service.deviceId ?? "";

      if (!deviceId) continue;

      // Normalize deviceId format if needed
      const normalizedDeviceId = deviceId.includes("#DE#")
        ? `DE#${deviceId.split("#DE#")[1]}`
        : deviceId.startsWith("DE#")
        ? deviceId
        : `DE#${deviceId}`;

      if (!map.has(normalizedDeviceId)) {
        map.set(normalizedDeviceId, []);
      }

      map.get(normalizedDeviceId)!.push(service as Service);
    }

    return map;
  }, [data]);

  return { servicesByDeviceId, loading, refetch };
};
